import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { serviceLandingList } from '../data/serviceLandings';
import { blogArticles } from '../data/blogArticles';
import { breadcrumbJsonLd, homeServiceJsonLd, reviewJsonLd, serviceJsonLd } from '../utils/schema';

import HomePage from '../pages/HomePage.vue';
import LoginPage from '../pages/LoginPage.vue';
import RegisterPage from '../pages/RegisterPage.vue';
import ServicesPage from '../pages/ServicesPage.vue';
import CreateOrderPage from '../pages/CreateOrderPage.vue';
import OrdersPage from '../pages/OrdersPage.vue';
import OrderDetailPage from '../pages/OrderDetailPage.vue';
import ProfilePage from '../pages/ProfilePage.vue';
import BlogPage from '../pages/BlogPage.vue';
import BlogArticlePage from '../pages/BlogArticlePage.vue';

import AdminLayout from '../AdminLayout.vue';
import AdminDashboard from '../pages/admin/AdminDashboard.vue';
import AdminServices from '../pages/admin/AdminServices.vue';
import AdminOrders from '../pages/admin/AdminOrders.vue';
import AdminOrderDetail from '../pages/admin/AdminOrderDetail.vue';
import AdminUsers from '../pages/admin/AdminUsers.vue';
import AdminReports from '../pages/admin/AdminReports.vue';

const serviceLandingSlugs = new Set(serviceLandingList.map((item) => item.slug));
const blogArticleSlugs = new Set(blogArticles.map((item) => item.slug));

const routes = [
  {
    path: '/',
    name: 'Home',
    component: HomePage,
    meta: {
      seo: {
        title: 'Ремонт телефонів у Харкові | MobiFIX PRO — швидкий ремонт від 350 грн',
        description: 'Терміновий ремонт смартфонів у Харкові на проспекті Ювілейний 57а. Ремонт телефона, заміна екрану, батареї, ремонт після води. Безкоштовна діагностика, гарантія до 12 місяців, оригінальні запчастини. Відстеження ремонту онлайн. Записатися через Telegram або за тел. +38 (068) 277-28-22.',
        keywords: [
          'ремонт телефонів Харків',
          'ремонт смартфонів Харків',
          'ремонт телефона Харьков',
          'ремонт телефонов Харьков',
          'ремонт iPhone Харків',
          'заміна екрану Харків',
          'ремонт після води Харків'
        ],
        path: '/',
        jsonLd: [homeServiceJsonLd(), reviewJsonLd()]
      }
    }
  },
  {
    path: '/login',
    name: 'Login',
    component: LoginPage,
    meta: {
      requiresGuest: true,
      seo: {
        title: 'Вхід до кабінету MobiFIX PRO',
        description: 'Увійдіть до кабінету MobiFIX PRO, щоб переглядати свої замовлення та статус ремонту.',
        keywords: ['вхід MobiFIX PRO', 'кабінет ремонту телефону'],
        path: '/login',
        robots: 'noindex,follow'
      }
    }
  },
  {
    path: '/register',
    name: 'Register',
    component: RegisterPage,
    meta: {
      requiresGuest: true,
      seo: {
        title: 'Реєстрація у MobiFIX PRO',
        description: 'Створіть акаунт MobiFIX PRO для відстеження ремонту та швидкого оформлення заявок.',
        keywords: ['реєстрація MobiFIX PRO', 'обліковий запис ремонту телефону'],
        path: '/register',
        robots: 'noindex,follow'
      }
    }
  },
  {
    path: '/services',
    name: 'Services',
    component: ServicesPage,
    meta: {
      seo: {
        title: 'Послуги ремонту телефонів у Харкові',
        description: 'Ремонт iPhone, Samsung, Xiaomi та інших телефонів у Харкові: екрани, батареї, зарядка, вода, камери та ремонт плати.',
        keywords: [
          'ремонт телефонів Харків',
          'ремонт смартфонів Харків',
          'ремонт телефона Харьков',
          'ремонт телефонов Харьков',
          'заміна батареї Харків',
          'ремонт Samsung Харків',
          'ремонт Xiaomi Харків'
        ],
        path: '/services',
        jsonLd: [
          reviewJsonLd(),
          serviceJsonLd({
            name: 'Послуги ремонту телефонів у Харкові',
            description: 'Ремонт телефонів, заміна екрана, батареї, розʼєму зарядки та відновлення після води у Харкові.',
            path: '/services'
          }),
          breadcrumbJsonLd([
            { name: 'Головна', path: '/' },
            { name: 'Послуги', path: '/services' }
          ])
        ]
      }
    }
  },
  {
    path: '/services/:slug',
    name: 'ServiceLanding',
    component: ServicesPage,
    props: true,
    meta: {
      seo: {
        title: 'Послуги ремонту телефонів у Харкові',
        description: 'Посадкова сторінка під конкретний тип ремонту або бренд смартфона у Харкові.',
        keywords: ['ремонт телефонів Харків', 'ремонт телефона Харьков', 'ремонт телефонов Харьков'],
        path: '/services'
      }
    }
  },
  {
    path: '/blog',
    name: 'Blog',
    component: BlogPage,
    meta: {
      seo: {
        title: 'Блог про ремонт телефонів у Харкові',
        description: 'Корисні статті MobiFIX PRO про ремонт iPhone, Samsung, Xiaomi, заміну батареї, екрана та відновлення після води.',
        keywords: [
          'блог ремонт телефонів Харків',
          'що робити якщо iPhone впав у воду',
          'скільки коштує заміна скла iPhone 14',
          'як вибрати акумулятор для Samsung'
        ],
        path: '/blog',
        jsonLd: [
          breadcrumbJsonLd([
            { name: 'Головна', path: '/' },
            { name: 'Блог', path: '/blog' }
          ])
        ]
      }
    }
  },
  {
    path: '/blog/:slug',
    name: 'BlogArticle',
    component: BlogArticlePage,
    props: true,
    meta: {
      seo: {
        title: 'Стаття блогу MobiFIX PRO',
        description: 'Корисна стаття про ремонт телефону від сервісу MobiFIX PRO.',
        keywords: ['ремонт телефонів блог'],
        path: '/blog'
      }
    }
  },
  {
    path: '/orders/create',
    name: 'CreateOrder',
    component: CreateOrderPage,
    meta: {
      seo: {
        title: 'Замовити ремонт телефону',
        description: 'Оформіть заявку на ремонт телефону онлайн. Менеджер MobiFIX PRO швидко звʼяжеться з вами телефоном або в месенджері.',
        keywords: ['замовити ремонт телефону Харків', 'ремонт телефону заявка онлайн'],
        path: '/orders/create',
        robots: 'noindex,follow'
      }
    }
  },
  {
    path: '/orders',
    name: 'Orders',
    component: OrdersPage,
    meta: {
      requiresAuth: true,
      seo: {
        title: 'Мої замовлення',
        description: 'Перегляд заявок і статусів ремонту у кабінеті MobiFIX PRO.',
        keywords: ['мої замовлення MobiFIX PRO'],
        path: '/orders',
        robots: 'noindex,follow'
      }
    }
  },
  {
    path: '/orders/:id',
    name: 'OrderDetail',
    component: OrderDetailPage,
    meta: {
      requiresAuth: true,
      seo: {
        title: 'Деталі замовлення',
        description: 'Сторінка детальної інформації по заявці на ремонт у MobiFIX PRO.',
        keywords: ['деталі замовлення ремонту'],
        path: '/orders',
        robots: 'noindex,follow'
      }
    }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: ProfilePage,
    meta: {
      requiresAuth: true,
      seo: {
        title: 'Профіль користувача',
        description: 'Профіль клієнта MobiFIX PRO з контактними даними та історією заявок.',
        keywords: ['профіль MobiFIX PRO'],
        path: '/profile',
        robots: 'noindex,follow'
      }
    }
  },
  {
    path: '/order/new',
    redirect: '/orders/create'
  },
  {
    path: '/repair',
    redirect: '/services'
  },
  {
    path: '/admin',
    component: AdminLayout,
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      seo: {
        title: 'Адмінпанель MobiFIX PRO',
        description: 'Внутрішня адмінпанель керування сервісом.',
        keywords: ['admin mobifix'],
        path: '/admin',
        robots: 'noindex,nofollow'
      }
    },
    children: [
      { path: '', name: 'AdminDashboard', component: AdminDashboard },
      { path: 'services', name: 'AdminServices', component: AdminServices },
      { path: 'orders', name: 'AdminOrders', component: AdminOrders },
      { path: 'orders/:id', name: 'AdminOrderDetail', component: AdminOrderDetail },
      { path: 'users', name: 'AdminUsers', component: AdminUsers },
      { path: 'reports', name: 'AdminReports', component: AdminReports }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition;
    return { top: 0, behavior: 'smooth' };
  }
});

router.beforeEach((to, from, next) => {
  const authStore = useAuthStore();

  if (to.meta.requiresAuth && !authStore.token) {
    next('/login');
    return;
  }

  if (to.meta.requiresGuest && authStore.token) {
    next('/');
    return;
  }

  if (to.meta.requiresAdmin) {
    if (!authStore.token) {
      next('/login');
      return;
    }

    if (authStore.user?.role !== 'admin') {
      next('/');
      return;
    }
  }

  if (to.name === 'ServiceLanding' && !serviceLandingSlugs.has(to.params.slug)) {
    next('/services');
    return;
  }

  if (to.name === 'BlogArticle' && !blogArticleSlugs.has(to.params.slug)) {
    next('/blog');
    return;
  }

  next();
});

export default router;
